'use client'

import { useMemo, useState } from 'react'

import {
  ColumnDef,
  createColumnHelper,
  PaginationState,
} from '@tanstack/react-table'
import { Archive, CircleCheck, CircleX, PencilIcon } from 'lucide-react'

import { RemoveImportedAccountDialog } from '~/app/accounting/RemoveImportedAccountDialog'
import { EMPTY_VALUE } from '~/app/accounting/display-utils'
import {
  ImportedAccount,
  ImportStatus,
  useImportedAccounts,
} from '~/app/accounting/useImportedAccounts'
import { Table } from '~/components/Table'
import {
  TABLE_ACTION_COLUMN_META,
  TABLE_ACTION_COLUMN_WIDTH,
  TableActionTrigger,
} from '~/components/TableActions'
import { RenameAccountDialog } from '~/components/account-detail/AccountActionsMenu/RenameAccountDialog'
import {
  AddressDisplay,
  LoadingSpinner,
  Timestamp,
  Tooltip,
} from '~/components/component-library'
import { Dropdown } from '~/components/component-library/Dropdown'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrgMember } from '~/hooks/org'

const INITIAL_PAGE_SIZE = 10
const NAME_COLUMN_WIDTH = 320
const DATE_COLUMN_WIDTH = 180
const STATUS_COLUMN_WIDTH = 140

const columnHelper = createColumnHelper<ImportedAccount>()

const ImportStatusCell = ({ status }: { status: ImportStatus }) => {
  if (status === 'completed') {
    return (
      <div className="flex items-center gap-2">
        <CircleCheck className="h-4 w-4 text-green-500" />
        Imported
      </div>
    )
  }

  if (status === 'failed') {
    return (
      <Tooltip
        className="relative inline-flex"
        content="Some networks failed to import. Remove this account to import the address again, or contact support."
      >
        <div className="flex items-center gap-2 text-error">
          <CircleX className="h-4 w-4" />
          Failed
        </div>
      </Tooltip>
    )
  }

  return (
    <div className="flex items-center gap-2 text-secondary">
      <LoadingSpinner size="lg" thickness="normal" variant="default" />
      Indexing
    </div>
  )
}

const ActionsCell = ({ account }: { account: ImportedAccount }) => {
  const { setGlobalModalState, closeGlobalModal } = useAuth()
  const orgMember = useActiveOrgMember()
  // Renaming is owner-only on the server.
  if (orgMember?.role !== 'OWNER') return null

  return (
    <div className="flex justify-end">
      <Dropdown
        align="end"
        side="bottom"
        items={[
          {
            content: 'Rename',
            icon: <PencilIcon />,
            onClick: () =>
              setGlobalModalState({
                isOpen: true,
                content: (
                  <RenameAccountDialog
                    smartAccount={account}
                    closeDialog={closeGlobalModal}
                  />
                ),
              }),
          },
          {
            content: 'Remove...',
            icon: <Archive />,
            onClick: () =>
              setGlobalModalState({
                isOpen: true,
                content: (
                  <RemoveImportedAccountDialog
                    smartAccount={account}
                    closeDialog={closeGlobalModal}
                  />
                ),
              }),
          },
        ]}
      >
        <TableActionTrigger aria-label="Imported account actions" />
      </Dropdown>
    </div>
  )
}

export const ImportedAccountsTab = () => {
  const { importedAccounts, isLoading } = useImportedAccounts()

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: INITIAL_PAGE_SIZE,
  })

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('name', {
          header: 'Name',
          size: NAME_COLUMN_WIDTH,
          maxSize: NAME_COLUMN_WIDTH,
          cell: (ctx) => ctx.getValue(),
          enableColumnFilter: false,
        }),
        columnHelper.accessor('address', {
          header: 'Address',
          cell: function AddressCell(ctx) {
            return (
              <AddressDisplay
                address={ctx.getValue()}
                layout="standard"
                size="small"
              />
            )
          },
          enableColumnFilter: false,
        }),
        columnHelper.accessor('importStatus', {
          header: 'Status',
          size: STATUS_COLUMN_WIDTH,
          maxSize: STATUS_COLUMN_WIDTH,
          cell: (ctx) => <ImportStatusCell status={ctx.getValue()} />,
          enableColumnFilter: false,
        }),
        columnHelper.accessor('importCutoffAt', {
          header: 'Cutoff date',
          size: DATE_COLUMN_WIDTH,
          maxSize: DATE_COLUMN_WIDTH,
          cell: function CutoffCell(ctx) {
            const cutoffAt = ctx.getValue()
            if (!cutoffAt) return EMPTY_VALUE
            return <Timestamp timestamp={cutoffAt} type="absolute" abbreviate />
          },
          enableColumnFilter: false,
        }),
        columnHelper.accessor('createdAt', {
          header: 'Imported',
          size: DATE_COLUMN_WIDTH,
          maxSize: DATE_COLUMN_WIDTH,
          cell: function CreatedCell(ctx) {
            return (
              <Timestamp
                timestamp={ctx.getValue()}
                type="absolute"
                abbreviate
              />
            )
          },
          enableColumnFilter: false,
        }),
        columnHelper.display({
          id: 'actions',
          header: '',
          ...TABLE_ACTION_COLUMN_WIDTH,
          cell: ({ row }) => <ActionsCell account={row.original} />,
          meta: TABLE_ACTION_COLUMN_META,
        }),
      ] as ColumnDef<ImportedAccount>[],
    [],
  )

  return (
    <Table
      columns={columns}
      data={importedAccounts}
      isLoading={isLoading}
      emptyMessage="Your workspace has no imported accounts"
      state={{ pagination }}
      onPaginationChange={setPagination}
      pagination={{
        enabled: true,
        initialPageSize: INITIAL_PAGE_SIZE,
      }}
    />
  )
}
